import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay, tap } from 'rxjs/operators';

import CustomURIEncoder from 'src/app/helpers/utils/CustomURIEncoder';
import retryApiError from 'src/app/helpers/utils/retryApiError';

import { StorageService } from 'src/app/services/storage/storage.service';

import LocalizationItem, { LocalizationItemParams, LocalizationType } from 'src/app/shared/models/localizationItem.model';

import { environment } from 'src/environments/environment';

import { getLocalizationItems } from './localization.collector';

const isId = (str: string): boolean => str.search(/([0-9]{8}-[0-9]{6}-[0-9]{3})\w+/g) !== -1;

@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  public localization: object = null;
  public versionUpdate = new BehaviorSubject<number>(0);
  public lang: BehaviorSubject<string> = new BehaviorSubject<string>('en');

  private version = 0;
  private readonly localizationPreparedData: Record<string, unknown> = {};

  private readonly countriesNamesAndCodes = [
    { countryName: 'Ireland', countryCode: 'ie' },
    { countryName: 'United Kingdom', countryCode: 'gb' },
    { countryName: 'Philippines', countryCode: 'ph' },
    { countryName: 'Australia', countryCode: 'au' },
    { countryName: 'India', countryCode: 'in' },
    { countryName: 'Bangladesh', countryCode: 'bd' },
    { countryName: 'Botswana', countryCode: 'bw' },
    { countryName: 'Brunei', countryCode: 'bn' },
    { countryName: 'South Africa', countryCode: 'za' },
    { countryName: 'Cyprus', countryCode: 'cy' },
    { countryName: 'Ghana', countryCode: 'gh' },
    { countryName: 'Kenya', countryCode: 'ke' },
    { countryName: 'New Zealand', countryCode: 'nz' },
    { countryName: 'Malta', countryCode: 'mt' },
    { countryName: 'Nepal', countryCode: 'np' },
    { countryName: 'Pakistan', countryCode: 'pk' },
    { countryName: 'Singapore', countryCode: 'sg' },
    { countryName: 'Sri Lanka', countryCode: 'lk' },
    { countryName: 'Tanzania', countryCode: 'tz' },
    { countryName: 'Uganda', countryCode: 'ug' },
    { countryName: 'Rwanda', countryCode: 'rw' },
    { countryName: 'Malaysia', countryCode: 'my' },
    { countryName: 'Barbados', countryCode: 'bb' },
    { countryName: 'Zambia', countryCode: 'zm' },
    { countryName: 'Nigeria', countryCode: 'ng' },
    { countryName: 'United States', countryCode: 'us' },
    { countryName: 'Portugal', countryCode: 'pt' },
    { countryName: 'Ukraine', countryCode: 'ua' },
    { countryName: 'Taiwan', countryCode: 'tw' },
    { countryName: 'Finland', countryCode: 'fi' },
    { countryName: 'Indonesia', countryCode: 'id' },
    { countryName: 'Denmark', countryCode: 'dk' },
    { countryName: 'Mexico', countryCode: 'mx' },
    { countryName: 'Norway', countryCode: 'no' },
    { countryName: 'Hungary', countryCode: 'hu' },
    { countryName: 'Czech Republic', countryCode: 'cz' },
    { countryName: 'Sweden', countryCode: 'se' },
    { countryName: 'Thailand', countryCode: 'th' },
    { countryName: 'Poland', countryCode: 'pl' },
    { countryName: 'Vietnam', countryCode: 'vn' },
    { countryName: 'Canada', countryCode: 'ca' },
    { countryName: 'Japan', countryCode: 'jp' },
    { countryName: 'Germany', countryCode: 'de' },
    { countryName: 'France', countryCode: 'fr' },
    { countryName: 'China', countryCode: 'cn' },
    { countryName: 'Netherlands', countryCode: 'nl' },
    { countryName: 'Brazil', countryCode: 'br' },
    { countryName: 'Spain', countryCode: 'es' },
    { countryName: 'Italy', countryCode: 'it' },
    { countryName: 'Russia', countryCode: 'ru' },
    { countryName: 'United Arab Emirates', countryCode: 'ae' },
    { countryName: 'Turkey', countryCode: 'tr' },
    { countryName: 'Belgium', countryCode: 'be' },
    { countryName: 'Switzerland', countryCode: 'ch' },
    { countryName: 'Hong Kong', countryCode: 'hk' },
    { countryName: 'Israel', countryCode: 'il' },
    { countryName: 'Georgia', countryCode: 'ge' },
    { countryName: 'Kazakhstan', countryCode: 'kz' },
    { countryName: 'Korea, Republic of', countryCode: 'kr' },
    { countryName: 'Slovenia', countryCode: 'si' },
    { countryName: 'Serbia', countryCode: 'rs' },
    { countryName: 'Estonia', countryCode: 'ee' },
    { countryName: 'Latvia', countryCode: 'lv' },
    { countryName: 'Cambodia', countryCode: 'kh' },
    { countryName: 'Romania', countryCode: 'ro' },
    { countryName: 'Bulgaria', countryCode: 'bg' },
    { countryName: 'Lithuania', countryCode: 'lt' },
    { countryName: 'Greece', countryCode: 'gr' },
    { countryName: 'Slovakia', countryCode: 'sk' },
    { countryName: 'Croatia', countryCode: 'hr' },
    { countryName: 'Qatar', countryCode: 'qa' },
    { countryName: 'Namibia', countryCode: 'na' },
    { countryName: 'Mozambique', countryCode: 'mz' },
    { countryName: 'Austria', countryCode: 'at' },
    { countryName: 'Ivory Coast', countryCode: 'ci' },
    { countryName: 'Madagascar', countryCode: 'mg' },
    { countryName: 'Mauritius', countryCode: 'mu' },
    { countryName: 'Senegal', countryCode: 'sn' },
    { countryName: 'Venezuela', countryCode: 've' },
    { countryName: 'Puerto Rico', countryCode: 'pr' },
    { countryName: 'Peru', countryCode: 'pe' },
    { countryName: 'Panama', countryCode: 'pa' },
    { countryName: 'Guatemala', countryCode: 'gt' },
    { countryName: 'Ecuador', countryCode: 'ec' },
    { countryName: 'Colombia', countryCode: 'co' },
    { countryName: 'Chile', countryCode: 'cl' },
    { countryName: 'Paraguay', countryCode: 'py' },
    { countryName: 'Argentina', countryCode: 'ar' },
    { countryName: 'Uruguay', countryCode: 'uy' },
    { countryName: 'Lebanon', countryCode: 'lb' },
    { countryName: 'Morocco', countryCode: 'ma' },
    { countryName: 'Algeria', countryCode: 'dz' },
    { countryName: 'Tunisia', countryCode: 'tn' },
    { countryName: 'Oman, Sultanate of', countryCode: 'om' },
    { countryName: 'Egypt', countryCode: 'eg' },
    { countryName: 'Iraq', countryCode: 'iq' },
    { countryName: 'Jordan', countryCode: 'jo' },
    { countryName: 'Kuwait', countryCode: 'kw' },
    { countryName: 'Saudi Arabia', countryCode: 'sa' },
    { countryName: 'Luxembourg', countryCode: 'lu' },
    { countryName: 'Costa Rica', countryCode: 'cr' },
    { countryName: 'El Salvador', countryCode: 'sv' },
    { countryName: 'Monaco', countryCode: 'mc' },
    { countryName: 'Bahrain', countryCode: 'bh' },
    { countryName: 'Trinidad and Tobago', countryCode: 'tt' },
    { countryName: 'Gibraltar', countryCode: 'gi' },
    { countryName: 'Iceland', countryCode: 'is' },
    { countryName: 'Angola', countryCode: 'ao' },
    { countryName: 'Azerbaijan', countryCode: 'az' },
    { countryName: 'Djibouti', countryCode: 'dj' },
    { countryName: 'Jamaica', countryCode: 'jm' },
    { countryName: 'Belarus', countryCode: 'by' },
    { countryName: 'Tajikistan', countryCode: 'tj' },
    { countryName: 'Gabon', countryCode: 'ga' },
    { countryName: 'Guinea', countryCode: 'gn' },
    { countryName: 'Dominican Republic', countryCode: 'do' },
    { countryName: 'Moldova', countryCode: 'md' },
    { countryName: 'Guyana', countryCode: 'gy' },
    { countryName: 'Zimbabwe', countryCode: 'zw' },
    { countryName: 'Cayman Islands', countryCode: 'ky' },
    { countryName: 'Honduras', countryCode: 'hn' }
  ];

  constructor(
    private readonly http: HttpClient,
    private readonly storage: StorageService
  ) {
    this.localizationPreparedData = getLocalizationItems();
    const regusLocalization = storage.get('regusLocalization');
    const regusLocalizationMoment = storage.get('regusLocalizationMoment');
    if (regusLocalization && regusLocalizationMoment) {
      if (
        Number.parseInt(moment().format('X'), 10) < Number.parseInt(regusLocalizationMoment, 10) &&
        Object.keys(this.localizationPreparedData).join() === Object.keys(regusLocalization).join()
      ) {
        this.localization = JSON.parse(regusLocalization);
      } else {
        storage.remove('regusLocalization');
        storage.remove('regusLocalizationMoment');
      }
    }
  }

  public getTranslations(params): Observable<unknown> {
    const queryParams = new URLSearchParams(params);
    const locale = queryParams.get('locale') || queryParams.get('culture');

    let currentCountryName = '',
      currentCountryCode = '';
    if (queryParams.get('culture')) {
      const cultureSecondPart = locale.split('-')[1];
      const currentCountryNameAndCode = this.countriesNamesAndCodes.find(
        (c) => c.countryCode.toLowerCase() == cultureSecondPart.toLowerCase()
      );
      if (currentCountryNameAndCode) {
        currentCountryName = currentCountryNameAndCode.countryName;
        currentCountryCode = currentCountryNameAndCode.countryCode;
      }
    }

    const countryName = queryParams.get('countryName') || currentCountryName;
    const countryCode = queryParams.get('countryCode') || currentCountryCode.toUpperCase();
    if (
      locale !== this.storage.get('locale') ||
      countryName !== this.storage.get('countryName') ||
      countryCode !== this.storage.get('countryCode')
    ) {
      this.localization = null;
      this.storage.set('locale', locale);
      this.storage.set('countryName', countryName);
      this.storage.set('countryCode', countryCode);
    }

    if (this.localization !== null) {
      this.incrementVersion();
      return new Observable((observer) => {
        observer.next(this.localization);
        observer.complete();
      });
    } else {
      const params = new HttpParams({ encoder: new CustomURIEncoder() })
        .set('locale', locale)
        .set('countryName', countryName)
        .set('countryCode', countryCode);
      const dataToApi = this.localDataToApiData(this.localizationPreparedData, queryParams);
      return this.http.post(`${environment.apiUrl}content/query`, dataToApi, { params }).pipe(
        delay(1000),
        retryApiError(500, 3),
        tap((data) => {
          this.localization = this.apiDataToLocalData(this.localizationPreparedData, data);
          this.storage.set('regusLocalization', JSON.stringify(this.localization));
          this.storage.set(
            'regusLocalizationMoment',
            moment().add(environment.localizationItemsLifeTime, 'minutes').format('X')
          );
          this.incrementVersion();
        })
      );
    }
  }

  private readonly getLocalizationItemId = (item): string => (typeof item === 'string' ? item : item.Id);

  private readonly assignParams = (prms: LocalizationItemParams, queryParams: URLSearchParams): LocalizationItemParams => {
    const params = Object.assign({}, prms);

    const locale = queryParams.get('locale') || queryParams.get('culture');

    let currentCountryName = '';
    if (queryParams.get('culture')) {
      const cultureSecondPart = locale.split('-')[1];
      const currentCountryNameAndCode = this.countriesNamesAndCodes.find(
        (c) => c.countryCode.toLowerCase() == cultureSecondPart.toLowerCase()
      );
      if (currentCountryNameAndCode) {
        currentCountryName = currentCountryNameAndCode.countryName;
      }
    }

    if (params.LanguageCode) {
      params.LanguageCode = locale || 'en-GB';
    }

    if (params.CountryName) {
      const countryName = queryParams.get('countryName');
      params.CountryName = countryName ? countryName : currentCountryName ? currentCountryName : 'United Kingdom';
    }

    return params;
  };

  private localDataToApiData(data: Record<string, unknown>, queryParams: URLSearchParams): Array<LocalizationItem> {
    const response = [];
    const addResponseItem = (v): void => {
      const Type = isId(v.Id ? v.Id : v) ? LocalizationType.Simple : LocalizationType.Predefined;
      response.push(
        typeof v === 'string'
          ? { Id: v, Type }
          : Object.assign(
              { Id: v.Id },
              v.Type ? { Type: v.Type } : { Type },
              v.Params ? { Params: this.assignParams(v.Params, queryParams) } : {}
            )
      );
    };
    Object.keys(Object.assign({}, data)).forEach((k) => {
      const v = data[k];
      if (Array.isArray(v)) {
        v.filter((item) => !item.separator).forEach(addResponseItem);
      } else {
        addResponseItem(v);
      }
    });
    return response.filter((row, index) => !response.slice(index + 1).find((row1) => row1.Id === row.Id));
  }

  private apiDataToLocalData(data, processedData = {}): Record<string, unknown> {
    const newData = {};
    const getLocalizedString = (item): string => {
      const id = this.getLocalizationItemId(item);
      if (processedData[id]) {
        return processedData[id];
      } else {
        return '';
      }
    };

    Object.keys(data).forEach((key) => {
      if (Array.isArray(data[key])) {
        let newValue = '';
        data[key].forEach((item) => {
          if (item.separator) {
            newValue += item.separator;
          } else {
            newValue += getLocalizedString(item);
          }
        });
        newData[key] = newValue;
      } else {
        newData[key] = getLocalizedString(data[key]);
      }
    });
    return newData;
  }

  private incrementVersion(): void {
    this.versionUpdate.next(++this.version);
  }
}
